import { useEffect, useMemo, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import assistant from '../services/assistant';

const CHAT_PAGE_SIZE = 60;

export const teamAssistantKeys = {
  syncMeta: ['team-assistant', 'sync-meta'],
  conversationFilters: ['team-assistant', 'conversation-filters'],
  messagesChat: (browse = {}) => ['team-assistant', 'messages', 'chat', normalizeBrowseKey(browse)],
  knowledgeOverview: ['team-assistant', 'knowledge-overview'],
  documents: (filters = {}) => ['team-assistant', 'documents', filters],
  knowledgeSearch: (filters = {}) => ['team-assistant', 'knowledge-search', filters],
  memories: (status = 'all') => ['team-assistant', 'memories', status],
};

export function normalizeBrowseKey(browse) {
  return {
    userId: browse?.userId ?? null,
    from: browse?.from ?? null,
    to: browse?.to ?? null,
    q: browse?.q ?? null,
    role: browse?.role ?? null,
    replyStatus: browse?.replyStatus ?? 'all',
  };
}

const STALE_MS = 2 * 60 * 1000;
const META_POLL_MS = 25 * 1000;

const unwrap = (res) => res?.data?.data;

async function fetchSyncMeta() {
  const res = await assistant.getSyncMeta();
  return unwrap(res);
}

async function fetchMessagePage({ limit, beforeId, browse }) {
  const res = await assistant.getMessages({
    limit,
    before_id: beforeId || undefined,
    user_id: browse?.userId ?? undefined,
    from: browse?.from ?? undefined,
    to: browse?.to ?? undefined,
    q: browse?.q ?? undefined,
    role: browse?.role ?? undefined,
  });
  const payload = unwrap(res);
  if (payload && Array.isArray(payload.messages)) {
    return payload;
  }
  if (Array.isArray(payload)) {
    return {
      messages: payload,
      has_more: false,
      oldest_id: payload[0]?.id ?? null,
      newest_id: payload[payload.length - 1]?.id ?? null,
      total: payload.length,
    };
  }
  return {
    messages: [],
    has_more: false,
    oldest_id: null,
    newest_id: null,
    total: 0,
  };
}

async function fetchConversationFilters() {
  const res = await assistant.getConversationFilters();
  return unwrap(res) || { staff: [], days: [] };
}

function flattenMessagePages(pages) {
  if (!pages?.length) return [];
  return [...pages].reverse().flatMap((p) => p.messages || []);
}

/**
 * Polls sync-meta; invalidates message queries only when the fingerprint changes.
 */
export function useTeamAssistantSyncPoll({ enabled = true, streaming = false } = {}) {
  const queryClient = useQueryClient();
  const lastFingerprint = useRef(null);

  const metaQuery = useQuery({
    queryKey: teamAssistantKeys.syncMeta,
    queryFn: fetchSyncMeta,
    enabled,
    staleTime: META_POLL_MS,
    refetchInterval: () => {
      if (!enabled || streaming || document.hidden) return false;
      return META_POLL_MS;
    },
    refetchOnWindowFocus: enabled && !streaming,
  });

  useEffect(() => {
    const fp = metaQuery.data?.fingerprint;
    if (!fp) return;

    if (lastFingerprint.current === null) {
      lastFingerprint.current = fp;
      return;
    }

    if (lastFingerprint.current !== fp) {
      lastFingerprint.current = fp;
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'messages', 'chat'] });
      queryClient.invalidateQueries({ queryKey: teamAssistantKeys.conversationFilters });
    }
  }, [metaQuery.data?.fingerprint, queryClient]);

  return metaQuery;
}

export function useTeamAssistantConversationFilters({ enabled = true } = {}) {
  return useQuery({
    queryKey: teamAssistantKeys.conversationFilters,
    queryFn: fetchConversationFilters,
    enabled,
    staleTime: STALE_MS,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: enabled,
  });
}

/** Team chat: latest window + optional older pages via loadOlder(). */
export function useTeamAssistantMessages({
  enabled = true,
  streaming = false,
  browse = {},
} = {}) {
  useTeamAssistantSyncPoll({ enabled, streaming });
  const browseKey = normalizeBrowseKey(browse);

  const infinite = useInfiniteQuery({
    queryKey: teamAssistantKeys.messagesChat(browseKey),
    enabled,
    staleTime: STALE_MS,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: enabled && !streaming,
    placeholderData: keepPreviousData,
    initialPageParam: null,
    queryFn: ({ pageParam }) =>
      fetchMessagePage({ limit: CHAT_PAGE_SIZE, beforeId: pageParam, browse: browseKey }),
    getNextPageParam: (lastPage) =>
      lastPage?.has_more && lastPage?.oldest_id ? lastPage.oldest_id : undefined,
  });

  const messages = useMemo(
    () => flattenMessagePages(infinite.data?.pages),
    [infinite.data?.pages]
  );

  const latestPage = infinite.data?.pages?.[0];
  const total = latestPage?.total ?? messages.length;

  return {
    data: messages,
    messages,
    isLoading: infinite.isLoading,
    isFetching: infinite.isFetching,
    hasOlder: Boolean(infinite.hasNextPage),
    loadOlder: () => infinite.fetchNextPage(),
    isLoadingOlder: infinite.isFetchingNextPage,
    totalCount: total,
    refetch: infinite.refetch,
  };
}

export function useInvalidateTeamAssistant() {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({ queryKey: ['team-assistant'] });
  };
}

export function useTeamAssistantKnowledgeOverview({ enabled = true } = {}) {
  return useQuery({
    queryKey: teamAssistantKeys.knowledgeOverview,
    queryFn: async () => {
      const res = await assistant.getKnowledgeOverview();
      return unwrap(res);
    },
    enabled,
    staleTime: STALE_MS,
  });
}

export function useTeamAssistantDocuments(filters, { enabled = true } = {}) {
  const key = {
    q: filters?.q?.trim() || null,
    user_id: filters?.userId ?? null,
    from: filters?.from ?? null,
    to: filters?.to ?? null,
    indexed: filters?.indexed ?? null,
    search_mode: filters?.searchMode ?? null,
    sort: filters?.sort ?? 'newest',
  };

  return useQuery({
    queryKey: teamAssistantKeys.documents(key),
    queryFn: async () => {
      const res = await assistant.listDocuments({
        q: key.q || undefined,
        user_id: key.user_id || undefined,
        from: key.from || undefined,
        to: key.to || undefined,
        indexed: key.indexed || undefined,
        search_mode: key.search_mode || undefined,
        sort: key.sort || undefined,
      });
      return unwrap(res) || [];
    },
    enabled,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

export function useDeleteTeamAssistantDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await assistant.deleteDocument(id);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'documents'] });
      queryClient.invalidateQueries({ queryKey: teamAssistantKeys.knowledgeOverview });
      queryClient.invalidateQueries({ queryKey: teamAssistantKeys.syncMeta });
    },
  });
}

export function useTeamAssistantKnowledgeSearch(filters, { enabled = false } = {}) {
  const key = {
    q: filters?.q?.trim() || null,
    type: filters?.type || 'all',
    user_id: filters?.userId ?? null,
    from: filters?.from ?? null,
    to: filters?.to ?? null,
  };

  return useQuery({
    queryKey: teamAssistantKeys.knowledgeSearch(key),
    queryFn: async () => {
      const res = await assistant.searchKnowledge({
        q: key.q || undefined,
        type: key.type || undefined,
        user_id: key.user_id || undefined,
        from: key.from || undefined,
        to: key.to || undefined,
      });
      return unwrap(res) || [];
    },
    enabled: enabled && Boolean(key.q || key.user_id || key.from || key.to || key.type !== 'all'),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useTeamAssistantMemories(status = 'all', { enabled = true } = {}) {
  return useQuery({
    queryKey: teamAssistantKeys.memories(status),
    queryFn: async () => {
      const res = await assistant.listMemories({
        status: status === 'all' ? undefined : status,
      });
      return unwrap(res) || [];
    },
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useCreateTeamAssistantMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body) => {
      const res = await assistant.createMemory(body);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'memories'] });
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'messages', 'chat'] });
      queryClient.invalidateQueries({ queryKey: teamAssistantKeys.syncMeta });
    },
  });
}

export function useConfirmTeamAssistantMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await assistant.confirmMemory(id);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'memories'] });
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'messages', 'chat'] });
      queryClient.invalidateQueries({ queryKey: teamAssistantKeys.syncMeta });
    },
  });
}

export function useArchiveTeamAssistantMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await assistant.archiveMemory(id);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'memories'] });
    },
  });
}

export function useUpdateTeamAssistantMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...body }) => {
      const res = await assistant.updateMemory(id, body);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'memories'] });
    },
  });
}

export function useDeleteTeamAssistantMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await assistant.deleteMemory(id);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'memories'] });
    },
  });
}

export function useUnarchiveTeamAssistantMemory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const res = await assistant.unarchiveMemory(id);
      return unwrap(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-assistant', 'memories'] });
    },
  });
}
